/**
 * Copyright 2024 ElectricSQL and contributors.
 * Copyright 2026 Improve Labs.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Derived from PGlite v0.5.4 (commit 6787212),
 * `packages/pglite/src/worker/index.ts`, function `acquireLock` (lines 745-756).
 *
 * MODIFICATIONS from the original:
 *   - Added an explicit `Promise<() => void>` return type (the original's
 *     `release` is implicitly typed and can be `undefined` to the type checker).
 *   - Named `acquireWebLock` to say which lock API it uses.
 *   - Added this doc comment explaining that the returned releaser is what makes
 *     the leadership-FAILED path in `run-leader-resource-host.web.ts` possible; PGlite
 *     never calls it, which is exactly why a PGlite worker whose `init()` fails
 *     holds the election lock forever and leaves every tab waiting.
 *   - The wait for the lock takes a deadline and an abort signal. Both withdraw
 *     the queued request itself (`navigator.locks.request`'s `signal`), so a lock
 *     granted after its waiter gave up is released at once instead of being held
 *     by nobody. The original waits for the lock with no way out.
 *
 * WEB-ONLY. Throws on any runtime lacking `navigator.locks` (no native code path
 * reaches this package; the throw is a loud failure rather than a silent no-op).
 */
import {
  boundedPromise,
  type Deadline,
  unboundedWait,
} from "@-/dev.improve.haniverse.lib.deadline";

/**
 * Take an exclusive Web Lock and return a releaser.
 *
 * The lock is held until the returned function is called OR the holding
 * context (worker/tab) dies — the browser's auto-release on death is the
 * correctness guarantee this whole package rests on.
 *
 * WAITING for the lock is bounded by `deadline` and by `signal`: when either
 * ends the wait, the queued request is withdrawn, so the lock can never be
 * granted to a waiter that has already given up. A wait that is legitimately
 * open-ended (a follower's worker queueing for leadership while another tab
 * leads) passes `unboundedWait(reason)`; nothing user-visible awaits that one.
 */
export async function acquireWebLock(
  lockId: string,
  { deadline, signal }: { deadline: Deadline; signal?: AbortSignal },
): Promise<() => void> {
  if (typeof navigator === "undefined" || navigator.locks === undefined) {
    throw new Error("leader-resource is web-only: navigator.locks is unavailable in this runtime");
  }
  let release!: () => void;
  await boundedPromise<void>(
    { deadline, operation: `acquire the Web Lock "${lockId}"` },
    (resolveAcquired, rejectAcquired) => {
      let acquired = false;
      // Withdraws the queued request. Aborted when the wait ends WITHOUT the lock (deadline or
      // caller abort), never after it was granted.
      const request = new AbortController();
      const onCallerAbort = (): void => {
        rejectAcquired(signal?.reason);
      };
      if (signal?.aborted === true) {
        onCallerAbort();
      } else {
        signal?.addEventListener("abort", onCallerAbort, { once: true });
      }
      navigator.locks
        .request(lockId, { mode: "exclusive", signal: request.signal }, async () => {
          if (request.signal.aborted) {
            // Granted in the same turn the waiter gave up: hand it straight back.
            return;
          }
          acquired = true;
          // Hand the caller a releaser, then keep the lock callback pending — the
          // lock is held for exactly as long as this promise is unresolved. Its
          // only awaiter is the browser's lock manager, which also releases it
          // when this context dies.
          const held = boundedPromise<void>(
            {
              deadline: unboundedWait(
                "a held Web Lock lasts until its owner releases it or its context dies; only the lock manager awaits it",
              ),
              operation: `hold the Web Lock "${lockId}"`,
            },
            (resolveHeld) => {
              release = () => {
                resolveHeld();
              };
            },
          );
          resolveAcquired();
          await held;
        })
        .catch((error: unknown) => {
          // Aborted requests reject here too; by then the wait has already settled.
          rejectAcquired(error);
        });
      return () => {
        signal?.removeEventListener("abort", onCallerAbort);
        if (!acquired) {
          request.abort(new Error(`Stopped waiting for the Web Lock "${lockId}"`));
        }
      };
    },
  );
  return release;
}
